import {
  VISUALIZATION_CONTENT_CONTAINER_CLASS_NAME,
  DEPENDENCY_MAP_NODES_CONTAINER_PADDING,
} from '@ardoq/global-consts';
import { ASSETS_AND_TEST_CONTAINER_ID } from '@ardoq/icons';
import { getImageDataURI, removeUnwantedNodes } from '../exportUtil';
import { HTML_EXPORT_PADDING, VIEW_LEGEND_SELECTOR } from '../consts';
import { ToastType, showToast } from '@ardoq/status-ui';
import type { ViewExporter } from './types';
import type { GetStyleSheetsFn, PartialCSSStyleSheet } from '../types';
import { getFontFaceRules } from '../htmlExport';
import { uniq } from 'lodash';

const setVisible = (element: HTMLElement, isVisible: boolean) => {
  element.style.visibility = isVisible ? 'visible' : 'hidden';
  element.childNodes.forEach(child => {
    if (child instanceof HTMLElement) {
      setVisible(child, isVisible);
    }
  });
};

/**
 * Sets the height of the cloned DOM after it has been inserted into the DOM and properly measured.
 */
const setViewHeight = (clonedDOM: HTMLElement) => {
  const contentNodeSelector = `.${VISUALIZATION_CONTENT_CONTAINER_CLASS_NAME}`;
  const clonedLegendContainer =
    clonedDOM.querySelector<HTMLElement>(VIEW_LEGEND_SELECTOR);
  const clonedContentContainer =
    clonedDOM.querySelector<HTMLElement>(contentNodeSelector);

  if (!clonedContentContainer || !clonedLegendContainer) {
    return;
  }

  // Now that the element is in the DOM, we can get accurate measurements
  const contentHeight =
    clonedContentContainer.scrollHeight +
    2 * DEPENDENCY_MAP_NODES_CONTAINER_PADDING;
  const legendHeight = clonedLegendContainer.scrollHeight;
  const finalHeight = Math.max(contentHeight, legendHeight);

  clonedDOM.style.height = `${finalHeight}px`;
};

/**
 * This function sets dimensions which will determine how the end result is laid out.
 */
const setViewContentDimensions = (
  originalContainerElement: HTMLElement,
  clonedDOM: HTMLElement
) => {
  const contentNodeSelector = `.${VISUALIZATION_CONTENT_CONTAINER_CLASS_NAME}`;

  const clonedLegendContainer =
    clonedDOM.querySelector<HTMLElement>(VIEW_LEGEND_SELECTOR);

  const clonedContentContainer =
    clonedDOM.querySelector<HTMLElement>(contentNodeSelector);

  // set the cloned stuff to the original dimensions
  const legendWidth =
    originalContainerElement.querySelector(VIEW_LEGEND_SELECTOR)?.clientWidth ||
    0;

  const originalContentElement =
    originalContainerElement.querySelector(contentNodeSelector);

  const isInitialized =
    clonedContentContainer && clonedLegendContainer && originalContentElement;

  if (!isInitialized) {
    return;
  }

  clonedContentContainer.style.minWidth = `${originalContentElement.scrollWidth}px`;
  clonedContentContainer.style.minHeight = `${originalContentElement.scrollHeight + HTML_EXPORT_PADDING * 2}px`;

  const { flexDirection } = getComputedStyle(originalContentElement);
  if (flexDirection === 'row') {
    clonedContentContainer.style.width = 'fit-content';
  }
  clonedContentContainer.style.height = 'fit-content';

  // measure the resulting dimensions
  const contentNodeWidth = originalContainerElement.scrollWidth;
  // set the dimensions explicitly
  clonedContentContainer.style.width = `${contentNodeWidth}px`;

  // place the legend to the right of the content
  const legendLeft =
    contentNodeWidth + 2 * DEPENDENCY_MAP_NODES_CONTAINER_PADDING;
  clonedLegendContainer.style.left = `${legendLeft}px`;
  clonedLegendContainer.style.top = `${DEPENDENCY_MAP_NODES_CONTAINER_PADDING}px`;
  clonedLegendContainer.style.margin = '0';
  // fit legend container to its content
  clonedLegendContainer.style.width = `${legendWidth}px`;
  clonedLegendContainer.style.height = 'fit-content';

  // set root node dimensions explicitly to avoid dimension changes in different containers
  // Width can be set now, but height needs to be measured after DOM insertion
  clonedDOM.style.width = `${legendLeft + legendWidth + DEPENDENCY_MAP_NODES_CONTAINER_PADDING}px`;
};

const getIconsStyles = (styleSheets: PartialCSSStyleSheet[]) => {
  const iconsStyleSheet = styleSheets.find(({ href }) =>
    href?.match(/\/icons\.?.*\.css$/)
  );
  if (!iconsStyleSheet) {
    return [];
  }
  return Array.from(iconsStyleSheet.cssRules).filter(
    rule => rule instanceof CSSStyleRule
  );
};

const matchesSelector = (element: Element, selector: string) =>
  element.matches(selector) || element.querySelector(selector);
const stripPseudoSelector = (selector: string) => selector.split('::')[0] ?? '';
const applicableCssRules = (
  rules: Iterable<CSSStyleRule>,
  container: Element
) =>
  Array.from(rules).filter(({ selectorText }) =>
    matchesSelector(container, stripPseudoSelector(selectorText))
  );

const applicableCssText = (rules: Iterable<CSSStyleRule>, container: Element) =>
  uniq(
    Array.from(applicableCssRules(rules, container)).map(
      ({ cssText }) => cssText
    )
  ).join('\r\n');

const exportCssClass = (classId: string, imageDataUri: string) => `
  img.${classId} {
    content: url(${imageDataUri});
  }
`;

type KeyedResult<K, R> = { key: K; result: R };
const keyedPromise = async <K, R>(
  key: K,
  promise: Promise<R>
): Promise<KeyedResult<K, R>> => {
  const result = await promise;
  return { key, result };
};

const INITIAL_CSS = '* { box-sizing: border-box }'; // use the alternative box model for the whole document. just like ardoq-front (via editorPane.less).

const exportCss = async (
  clonedDOM: HTMLElement,
  getStyleSheets: GetStyleSheetsFn
) => {
  const imageElements = clonedDOM.querySelectorAll('img');
  const imageClassesByUrl = new Map<string, string>();
  let serializedImageCount = 0;
  let css = INITIAL_CSS;
  const promisedImageDataUris: Promise<KeyedResult<string, string>>[] = [];
  for (const imageElement of imageElements) {
    const { src } = imageElement;
    if (!imageClassesByUrl.has(src)) {
      const classId = `image${++serializedImageCount}`;
      imageClassesByUrl.set(src, classId);
      promisedImageDataUris.push(keyedPromise(classId, getImageDataURI(src)));
    }
    imageElement.removeAttribute('src');
    imageElement.classList.add(imageClassesByUrl.get(src)!);
    imageElement.setAttribute('crossorigin', 'anonymous');
  }

  const imageDataUris = await Promise.all(promisedImageDataUris);
  for (const { key: classId, result: imageDataUri } of imageDataUris) {
    css += exportCssClass(classId, imageDataUri);
  }

  const styledComponents =
    document.querySelector<HTMLStyleElement>('style[data-styled]')?.sheet;

  if (styledComponents) {
    css += applicableCssText(
      Array.from(styledComponents.cssRules).filter(
        rule => rule instanceof CSSStyleRule
      ),
      clonedDOM
    );
  }

  const sheets = await getStyleSheets();
  css += applicableCssText(getIconsStyles(sheets), clonedDOM);
  css += uniq((await getFontFaceRules(sheets)).map(rule => rule.cssText)).join(
    '\r\n'
  );
  return css;
};

function* svgDefs(clonedDOM: HTMLElement) {
  for (const useElement of clonedDOM.querySelectorAll('use')) {
    const {
      href: { baseVal: href },
    } = useElement;
    const outerHTML = document.querySelector(href)?.outerHTML;
    if (!outerHTML) {
      continue;
    }
    yield outerHTML;
  }
}
const formattedSvgDefs = (root: HTMLElement) =>
  Array.from(svgDefs(root)).join('\r\n');

const exportHtmlView: ViewExporter = async (
  containerElement,
  getStyleSheets,
  labels
) => {
  const assetsAndTestContainer = document.getElementById(
    ASSETS_AND_TEST_CONTAINER_ID
  );

  if (!assetsAndTestContainer) {
    return null;
  }

  if (
    Math.max(containerElement.scrollHeight, containerElement.scrollWidth) > 5000
  ) {
    showToast(
      labels?.longExportToast ??
        "It can take some time to export this view. Please don't change the window zoom while the browser is working.",
      ToastType.INFO
    );
  }

  const clonedDOM = containerElement.cloneNode(true) as HTMLElement;
  // When SVG is rendered as an image (img.src), the foreignObject HTML has no
  // <body> ancestor, so inherited properties fall back to the SVG root's browser
  // defaults rather than the app's values: font-size to 16px (text renders larger
  // and overflows the height we measured from the live page) and font-family to
  // the default serif (text loses the Inter font). Pinning these from the live
  // container keeps the exported layout and fonts consistent with the live page.
  const { fontSize, lineHeight, fontFamily } =
    getComputedStyle(containerElement);
  clonedDOM.style.fontSize = fontSize;
  clonedDOM.style.lineHeight = lineHeight;
  clonedDOM.style.fontFamily = fontFamily;
  const css = await exportCss(clonedDOM, getStyleSheets);
  const defs = formattedSvgDefs(clonedDOM);
  removeUnwantedNodes(clonedDOM);
  clonedDOM.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');

  setViewContentDimensions(containerElement, clonedDOM);

  // #region measurements

  assetsAndTestContainer.appendChild(clonedDOM); // an element must be in the DOM in order to measure it.

  // Set the height after DOM insertion for accurate measurements
  setViewHeight(clonedDOM);

  const { scrollWidth: width, scrollHeight: height } = clonedDOM;

  const legendContent = Array.from(
    clonedDOM.querySelectorAll<HTMLElement>(VIEW_LEGEND_SELECTOR)
  );
  const viewContentMaxWidth = Math.max(
    ...legendContent.map(
      legendContentElement => legendContentElement.scrollWidth
    )
  );
  const viewContentMaxHeight = Math.max(
    ...legendContent.map(
      legendContentElement => legendContentElement.scrollHeight
    )
  );

  // #endregion

  assetsAndTestContainer.removeChild(clonedDOM);

  // make legends visible
  legendContent.forEach(legendContentElement =>
    setVisible(legendContentElement, true)
  );

  return {
    clonedDOM,
    css,
    defs,
    measurements: {
      width,
      height,
      viewContentMaxWidth,
      viewContentMaxHeight,
    },
  };
};
export { exportHtmlView };
